import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { startThemeController } from './lib/hooks/use-theme';

// Order matters: reset, scale, semantic contract, direction primitives, base,
// then component styles. Direction files are attribute scoped, so they win
// over the :root mappings without needing to be last.
import './styles/reset.css';
import './styles/tokens/scale.css';
import './styles/tokens/semantic.css';
import './styles/tokens/direction-instrument.css';
import './styles/tokens/direction-cleanroom.css';
// The atmosphere layer sits after the directions on purpose: its :root block is
// only a floor, and each direction's own block has to win. Marketing surface
// only, and the scope rule is written at the top of the file.
import './styles/tokens/atmosphere.css';
import './styles/base.css';
import './styles/components/shell.css';
import './styles/components/page.css';
import './styles/components/dock.css';

// Assert the font actually loaded rather than silently falling back to a
// system face. This is the BRAND-V2 lesson from the poster PDFs: a fallback
// face that nobody noticed is how a brand ships wrong.
//
// The assertion requests each face itself instead of waiting on
// document.fonts.ready. ready resolves once the faces currently IN USE have
// settled, and at boot the body is still an empty root div, so nothing is in
// use, every face is still "unloaded", and check() answers false. That
// reported both faces as missing on every single load even though both were
// fine, which teaches a reader to ignore the one warning that guards the
// brand. load() resolves with the faces it matched, so a real miss (file
// absent, wrong path, corrupt woff2) still reports and a healthy boot is
// silent.
if ('fonts' in document) {
  const assertFace = (spec: string, message: string) => {
    void document.fonts
      .load(spec)
      .then((faces) => {
        if (faces.length === 0 || faces.some((face) => face.status !== 'loaded')) {
          console.warn(message);
        }
      })
      .catch(() => console.warn(message));
  };

  assertFace(
    '500 16px Outfit',
    'SV Cloud: Outfit did not load. The page is rendering in a fallback face.',
  );
  assertFace(
    '400 16px "JetBrains Mono"',
    'SV Cloud: JetBrains Mono did not load. Machine facts are in a fallback face.',
  );
}

// The appearance loop, started before the first render and deliberately outside
// React. Three jobs: reconcile the rehydrated store against the operating
// system so nothing paints twice, keep the mirror the index.html bootstrap
// reads in step with the account preference, and adopt a theme the demo dock
// writes directly as an explicit choice, so the dock and the Appearance section
// can never disagree without either one knowing about the other.
startThemeController();

const container = document.getElementById('root');
if (!container) throw new Error('root element is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
